import { useMemo, useState } from "react";
import { Button } from "@app/components/ui/button";
import { Badge } from "@app/components/ui/badge";
import useMageXStore, { clearSearch, useSearchEdited } from "@app/zustand/store";
import {
  RefreshCw,
  Download,
  X,
  BrainCircuit,
  ChevronsUpDown,
  Check,
  FolderPlus,
  CheckCircle2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@app/components/ui/dialog";
import { Input } from "@app/components/ui/input";
import { Textarea } from "@app/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@app/components/ui/popover";
import { cn } from "@app/lib/utils";
import { LastSourceSynchro } from "@app/components/LastSourceSynchro";
import { ReliabilityInfoButton } from "@app/components/ReliabilityInfoButton";
import { Trans, useLingui } from "@lingui/macro";
import API from "@app/services/api";
import { usePatientSelector } from "@app/utils/use-patient-selector";
import { usePreviewView } from "@app/zustand/preview-view";
import { useNavigate, useParams } from "react-router";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@app/components/ui/command";
import { mapPatientsSelectV2CToTenantId } from "@app/utils/patient";
import useUser from "@app/zustand/user";
import { useReadonlyScope } from "@app/utils/use-readonly-scope";
import { useScope } from "@app/zustand/scope";
import { ScopeSelector } from "@app/components/ScopeSelector";
import PathwayBody, { type CohortSelection } from "@app/components/dashboard-pathway/PathwayBody";
import { usePathwayDashboard } from "@app/zustand/dashboard-pathway";
import {
  buildPathwayQuery,
  usePathwaySummary,
} from "@app/components/dashboard-pathway/usePathway";
import { viewAsHeaders } from "@app/services/view-as";
import {
  refusedAllowance,
  useExportShareCap,
} from "@app/utils/use-export-share-cap";
import { toast } from "sonner";
import {
  useHiddenFieldKeys,
  useShownFieldKeys,
} from "@app/zustand/field-visibility";
import { usePractitionerFilter } from "@app/utils/use-practitioner-filter";
import { PractitionerFilterHint } from "@app/components/PractitionerFilterHint";
import { SelectionFilterHint } from "@app/components/SelectionFilterHint";
import { isInspectorRole } from "@app/utils/inspector-role";
import { WithheldCount } from "@app/components/WithheldCount";

/**
 * The dashboard: the reader's perimeter over the exploration board, which reads the care pathway
 * (`/dashboard/pathway/*`) and counts in episodes. What acts on the selection — its filters, its
 * export, saving it as a cohort — sits on the board's counter line, beside the counts it acts on.
 */
export default function DashboardContent() {
  const { t } = useLingui();
  const filterParams = useMageXStore((state) => state.filterParams);
  const setFilterParams = useMageXStore((state) => state.setFilterParams);
  const nlSearchResult = useMageXStore((state) => state.nlSearchResult);
  const nlAppliedKey = useMageXStore((state) => state.nlAppliedKey);
  const setNlAppliedKey = useMageXStore((state) => state.setNlAppliedKey);
  const nlEdited = useSearchEdited();
  const patientsSelect = useMageXStore((state) => state.patientsSelect);
  const { previewRole, previewMedecin, setPreview } = usePreviewView();
  const user = useUser((state) => state.user);
  const userRole = (user as { role?: string } | null)?.role;
  const navigate = useNavigate();
  const params = useParams();
  const readonlyScope = useReadonlyScope();
  // A cohort is filed under the speciality on screen: a visitor of another one does not save (PT-74).
  const canSaveCohort = !readonlyScope;
  const hiddenFieldKeys = useHiddenFieldKeys();
  const shownFieldKeys = useShownFieldKeys();
  // An inspector (global persona, manufacturer's reader) is a pure anonymised-aggregate consumer
  // with no "my patients" notion.
  const isGlobalView = isInspectorRole(previewRole ?? userRole);
  // Data perimeter (mine vs whole speciality). Anonymised = cross-doctor view.
  const scope = useScope((s) => s.scope);
  const anonymised = scope === "specialite";
  // You may only open the named record of your own patients.
  const canSelectPatient = !isGlobalView && !anonymised;
  // The perimeter the board reads — the same rule as `PathwayBody`.
  const boardScope = isGlobalView || anonymised ? "specialite" : "own";

  // The rail's selection — the only place the board's population is written down.
  const pathwayConditions = usePathwayDashboard((state) => state.conditions);
  // Admins and MAGE-X reviewers only; narrows the board, its export, a saved cohort and the picker (PT-111).
  const practitioner = usePractitionerFilter();

  const [exporting, setExporting] = useState(false);
  const shareCap = useExportShareCap();

  // Cohort creation dialog, opened from the board with its own counts.
  const [showCohortDialog, setShowCohortDialog] = useState(false);
  const [cohortName, setCohortName] = useState("");
  const [cohortDescription, setCohortDescription] = useState("");
  const [cohortLoading, setCohortLoading] = useState(false);
  const [cohortSuccess, setCohortSuccess] = useState(false);
  const [cohortError, setCohortError] = useState<string | null>(null);
  const [cohortSelection, setCohortSelection] =
    useState<CohortSelection | null>(null);

  // « Vue médecin »: the previewed doctor's perimeter, counted on the pathway.
  const previewingDoctor =
    userRole === "admin" && previewRole === "medecin" && !!previewMedecin;
  const perimeterQuery = useMemo(() => ({ scope: boardScope }), [boardScope]);
  const perimeter = usePathwaySummary(perimeterQuery, !previewingDoctor);

  // The board's selection: the picker offers the patients its counter lists (PT-148).
  const pickerSelection = useMemo(
    () =>
      buildPathwayQuery(pathwayConditions, {
        periode: filterParams.periode,
        periodeStart: filterParams.periodeStart,
        periodeEnd: filterParams.periodeEnd,
      }),
    [pathwayConditions, filterParams.periode, filterParams.periodeStart, filterParams.periodeEnd],
  );

  const {
    open: patientSelectorOpen,
    setOpen: setPatientSelectorOpen,
    searchValue: patientSearchValue,
    setSearchValue: setPatientSearchValue,
    patients: selectorPatients,
    loading: selectorLoading,
    hasMore: selectorHasMore,
    loadMore: loadMoreSelectorPatients,
    narrowed: selectorNarrowed,
  } = usePatientSelector({ praticien: practitioner?.id, selection: pickerSelection });

  // Selected patient: check on-demand results first, fall back to background-loaded store.
  const selectedPatient = useMemo(() => {
    const source = selectorPatients.length > 0 ? selectorPatients : patientsSelect;
    return source?.find((p) => {
      if (!p || p.i == null) return false;
      return (
        p.i.toString() === params.id_patient &&
        mapPatientsSelectV2CToTenantId(p.c) === params.tenant_id
      );
    });
  }, [selectorPatients, patientsSelect, params.id_patient, params.tenant_id]);

  /**
   * The selection on screen, as a CSV of episodes — the query the board fetched with, so the file
   * carries exactly the filters in force.
   *
   * Fetched rather than linked: the « voir comme » headers cannot ride on an anchor, and without them
   * the server would export the reader's own perimeter while the screen shows a previewed one.
   */
  async function exportData() {
    const query = buildPathwayQuery(pathwayConditions, {
      periode: filterParams.periode,
      periodeStart: filterParams.periodeStart,
      periodeEnd: filterParams.periodeEnd,
      scope: boardScope,
      praticien: practitioner?.id,
    });
    setExporting(true);
    try {
      // Patients outside the reader's patientèle are capped (PT-120): asked first, refused by the server.
      if (!(await shareCap.confirm(query))) return;
      const response = await fetch(API.getUrl("/dashboard/pathway/export", query), {
        method: "GET",
        credentials: "include",
        headers: { Accept: "text/csv", ...viewAsHeaders() },
      });
      if (!response.ok) {
        const refused = await refusedAllowance(response);
        if (refused) {
          shareCap.showRefusal(refused);
        } else {
          toast.error(t`L'export a échoué. Veuillez réessayer.`);
        }
        return;
      }
      // The server names the file, and that name says which perimeter it holds. Read only when the
      // browser lets us: the dev front end talks to another origin, and a credentialed request there
      // exposes no response header, so the local name below is what keeps the two environments alike.
      const disposition = response.headers.get("Content-Disposition") ?? "";
      const served = /filename=([^;]+)/.exec(disposition)?.[1]?.trim();
      const stamp = new Date().toISOString().slice(0, 10);
      const url = window.URL.createObjectURL(await response.blob());
      const a = document.createElement("a");
      a.href = url;
      // Outside the reader's patientèle the file holds counts, not episodes (PT-144).
      a.download =
        served ||
        `${boardScope === "specialite" ? "mage-x-effectifs" : "mage-x-episodes"}-${stamp}.csv`;
      a.click();
      // After the click has been dispatched, not during: revoking synchronously races the download
      // in Chrome and leaves an empty file.
      window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
    } catch {
      toast.error(t`L'export a échoué. Veuillez réessayer.`);
    } finally {
      setExporting(false);
    }
  }

  function openCohortDialog(selection: CohortSelection) {
    const today = new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
    setCohortName(t`Cohorte — ${today}`);
    setCohortDescription("");
    setCohortSelection(selection);
    setCohortLoading(false);
    setCohortSuccess(false);
    setCohortError(null);
    setShowCohortDialog(true);
  }

  async function createCohort() {
    if (!cohortName.trim()) return;
    setCohortLoading(true);
    setCohortError(null);
    try {
      // A cohort is what the board shows: its pathway conditions and period. The server chooses its
      // dossiers by the same selection (PT-96).
      const res = await API.post({
        path: "/catalog/cohorte",
        body: {
          name: cohortName.trim(),
          description: cohortDescription.trim() || null,
          scope: "own",
          pathway: {
            conditions: pathwayConditions,
            periode: filterParams.periode ?? undefined,
            periodeStart: filterParams.periodeStart ?? undefined,
            periodeEnd: filterParams.periodeEnd ?? undefined,
            // Replayed with the cohort, or it would hold more patients than the board just counted.
            praticien: practitioner?.id,
          },
        },
      });
      if (res?.ok) {
        setCohortSuccess(true);
      } else {
        setCohortError(res?.error || t`Erreur lors de la création de la cohorte`);
      }
    } catch {
      setCohortError(t`Erreur lors de la création de la cohorte`);
    } finally {
      setCohortLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50/50">
      {/* ── Toolbar ── the perimeter the board reads. A global reader has none to choose, so the
          bar only appears there to carry a search in force. */}
      {(!isGlobalView || nlSearchResult) && (
        <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-slate-200/80 shadow-sm">
          {!isGlobalView && (
            <div className="px-6 py-3 flex items-center gap-3">
              {/* Data perimeter: my patientèle vs the whole speciality (anonymised) */}
              <ScopeSelector />

              {/* Opening a NAMED dossier, searched by name among the patients the board's selection
                  holds (PT-148). On a patientèle whose pathway layer has not been built yet the board
                  draws no counter and the server narrows nothing: this is the only way to a patient. */}
              {canSelectPatient && (
                <div className="inline-flex">
                <Popover open={patientSelectorOpen} onOpenChange={setPatientSelectorOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      role="combobox"
                      aria-expanded={patientSelectorOpen}
                      className="gap-1.5 text-sm text-slate-600 w-56 justify-between"
                    >
                      <span className="truncate">
                        {selectedPatient ? (
                          <span>{selectedPatient.n}</span>
                        ) : t`Sélectionner un patient`}
                      </span>
                      <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder={t`Rechercher un patient...`}
                        value={patientSearchValue}
                        onValueChange={setPatientSearchValue}
                      />
                      {practitioner && (
                        <PractitionerFilterHint label={practitioner.label} />
                      )}
                      {selectorNarrowed && <SelectionFilterHint />}
                      <CommandList
                        onScroll={(e) => {
                          const el = e.currentTarget;
                          if (selectorHasMore && !selectorLoading && el.scrollHeight - el.scrollTop <= el.clientHeight + 80) {
                            loadMoreSelectorPatients();
                          }
                        }}
                      >
                        {selectorLoading && selectorPatients.length === 0 ? (
                          <div className="flex items-center justify-center py-4 text-slate-400 text-sm gap-2">
                            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                            <Trans>Chargement...</Trans>
                          </div>
                        ) : (
                          <>
                            <CommandEmpty><Trans>Aucun patient trouvé.</Trans></CommandEmpty>
                            <CommandGroup>
                              {selectorPatients.map((patient) => (
                                <CommandItem
                                  key={`${patient.i}-${patient.c}`}
                                  value={patient.n}
                                  onSelect={() => {
                                    navigate(
                                      `/patient/${mapPatientsSelectV2CToTenantId(patient.c)}/${patient.i}`,
                                    );
                                    setPatientSelectorOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      selectedPatient?.i === patient.i &&
                                        selectedPatient?.c === patient.c
                                        ? "opacity-100"
                                        : "opacity-0",
                                    )}
                                  />
                                  <span>{patient.n}</span>
                                </CommandItem>
                              ))}
                              {selectorLoading && selectorPatients.length > 0 && (
                                <div className="flex items-center justify-center py-2 text-slate-400 text-sm gap-2">
                                  <RefreshCw className="h-3 w-3 animate-spin" />
                                </div>
                              )}
                            </CommandGroup>
                          </>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                </div>
              )}

              <div className="flex-1" />

              <LastSourceSynchro />
            </div>
          )}

          {/* The natural-language search in force. The board reports what it applied of it. */}
          {nlSearchResult && (
            <div
              className={cn(
                "px-6 pb-2.5 flex items-center gap-2 flex-wrap",
                isGlobalView && "pt-2.5",
              )}
            >
              <Badge
                variant="outline"
                className="gap-1 pl-2 pr-1 py-0.5 text-xs font-medium bg-primary/5 border-primary/20 text-primary"
              >
                <BrainCircuit className="h-3 w-3" />
                <span><Trans>Recherche IA : {nlSearchResult.explanation}</Trans></span>
                {nlEdited && (
                  <span className="font-normal text-slate-500">
                    · <Trans>sélection modifiée depuis la recherche</Trans>
                  </span>
                )}
                {/* The same as the bar's own cross: the search and every filter it set go together. */}
                <button
                  type="button"
                  onClick={clearSearch}
                  title={t`Effacer la recherche et ses filtres`}
                  aria-label={t`Effacer la recherche et ses filtres`}
                  className="ml-0.5 rounded-full p-0.5 hover:bg-primary/10 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            </div>
          )}
        </div>
      )}

      {/* ── Bannière "Vue médecin" : quel périmètre l'œil affiche réellement ──
           Le libellé suit le compte prévisualisé, pas le fait de prévisualiser : un relecteur de
           MAGE-X lit toute sa spécialité, et promettre « uniquement les dossiers du médecin »
           au-dessus d'une cohorte de spécialité décrirait le contraire de ce qui est à l'écran. ── */}
      {previewingDoctor && previewMedecin && (
          <div className="mx-6 mt-4 rounded-lg bg-blue-50 border border-primary/20 px-4 py-3 flex flex-wrap items-center gap-3">
            <span className="text-sm text-slate-700">
              {(previewMedecin.role === "medecin" || !previewMedecin.role) &&
              !previewMedecin.reviewer ? (
                <Trans>Vous affichez uniquement les dossiers du médecin</Trans>
              ) : (
                <Trans>Vous affichez la plateforme telle que la lit</Trans>
              )}{" "}
              <strong>
                {[previewMedecin.prenom, previewMedecin.nom]
                  .filter(Boolean)
                  .join(" ") || previewMedecin.email}
              </strong>{" "}
              ({perimeter.data ? (perimeter.data.total.patients ?? <WithheldCount />) : "…"}{" "}
              <Trans>dossier(s)</Trans>).
            </span>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 border-primary/30 text-primary hover:bg-primary/10"
              onClick={() => setPreview(null)}
            >
              <Trans>
                Voir tous les dossiers (Vue admin)
              </Trans>
            </Button>
          </div>
        )}

      {/* The board reads the CARE PATHWAY: episodes, the moments in them, and the ontology hanging
          off each moment. */}
      <PathwayBody
        periode={filterParams.periode}
        periodeStart={filterParams.periodeStart}
        periodeEnd={filterParams.periodeEnd}
        onPeriodTokenChange={(periode) =>
          setFilterParams({ periode, periodeStart: null, periodeEnd: null })
        }
        onPeriodChange={(periodeStart, periodeEnd) =>
          setFilterParams({ periode: null, periodeStart, periodeEnd })
        }
        // The search, the period and the bar's question: the board clears its own conditions too.
        onResetAll={clearSearch}
        onSaveCohort={canSaveCohort ? openCohortDialog : undefined}
        // Beside the counter it exports: the file is the selection that line counts. A button and not
        // a menu, because there is one thing to do — it turns while the file builds.
        actions={
          <button
            type="button"
            onClick={() => void exportData()}
            disabled={exporting}
            title={t`Exporter les données (CSV)`}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:border-primary/50 hover:text-primary disabled:pointer-events-none disabled:opacity-70"
          >
            {exporting ? (
              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
            {exporting ? (
              <Trans>Export en cours...</Trans>
            ) : (
              <Trans>Exporter</Trans>
            )}
          </button>
        }
        // The preview is a front-end simulation, and the backend honours a previewed role for
        // ADMIN sessions only — the demo account is a médecin, so its header is dropped. The gate
        // therefore lives on the same side as the toggle.
        isGlobalView={isGlobalView}
        nlFilters={nlSearchResult?.parsed_filters}
        nlUnmatched={nlSearchResult?.unmatched}
        nlChanges={nlSearchResult?.changes}
        nlBase={nlSearchResult?.base}
        nlApplied={nlAppliedKey}
        onSearchApplied={setNlAppliedKey}
        hiddenFieldKeys={hiddenFieldKeys}
        shownFieldKeys={shownFieldKeys}
      />

      <ReliabilityInfoButton />

      {/* Cohort creation dialog */}
      {shareCap.dialog}

      <Dialog open={showCohortDialog} onOpenChange={(open) => { if (!cohortLoading) setShowCohortDialog(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderPlus className="h-5 w-5 text-primary" />
              <Trans>Envoyer comme cohorte</Trans>
            </DialogTitle>
          </DialogHeader>

          {cohortSuccess ? (
            <div className="flex flex-col items-center gap-4 py-4">
              <CheckCircle2 className="h-12 w-12 text-green-500" />
              <p className="text-center text-slate-700 font-medium">
                <Trans>Cohorte créée avec succès !</Trans>
              </p>
              <p className="text-center text-slate-500 text-sm">
                <Trans>Vous pouvez la retrouver dans le catalogue.</Trans>
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowCohortDialog(false)}>
                  <Trans>Fermer</Trans>
                </Button>
                <Button onClick={() => { setShowCohortDialog(false); navigate("/catalogue"); }}>
                  <Trans>Voir dans le catalogue</Trans>
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4 py-2">
              {/* The board's own counts, patients AND episodes: that is what the cohort holds. */}
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-600">
                <Trans>
                  <span className="font-medium text-slate-800">
                    {cohortSelection?.patients ?? 0}
                  </span>{" "}
                  patient(s) ·{" "}
                  <span className="font-medium text-slate-800">
                    {cohortSelection?.episodes ?? 0}
                  </span>{" "}
                  épisode(s) inclus dans cette cohorte
                </Trans>
                {practitioner && (
                  <div className="mt-1.5 text-xs">
                    <Trans>
                      Praticien :{" "}
                      <span className="font-medium text-slate-800">
                        {practitioner.label}
                      </span>
                    </Trans>
                  </div>
                )}
                {nlSearchResult?.explanation && (
                  <div className="mt-1.5 flex items-center gap-1 text-xs text-primary">
                    <BrainCircuit className="h-3 w-3" />
                    {nlSearchResult.explanation}
                    {nlEdited && (
                      <span className="text-slate-500">
                        · <Trans>sélection modifiée depuis la recherche</Trans>
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-slate-700">
                  <Trans>Nom de la cohorte</Trans>
                  <span className="text-red-500 ml-0.5">*</span>
                </label>
                <Input
                  value={cohortName}
                  onChange={(e) => setCohortName(e.target.value)}
                  placeholder={t`Ex. : Cohorte rachis lombaire 2025`}
                  disabled={cohortLoading}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-slate-700">
                  <Trans>Description</Trans>
                  <span className="text-slate-400 text-xs ml-1">(<Trans>optionnel</Trans>)</span>
                </label>
                <Textarea
                  value={cohortDescription}
                  onChange={(e) => setCohortDescription(e.target.value)}
                  placeholder={t`Décrivez le contexte ou l'objectif de cette cohorte...`}
                  rows={3}
                  disabled={cohortLoading}
                />
              </div>

              {cohortError && (
                <p className="text-sm text-red-500">{cohortError}</p>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setShowCohortDialog(false)} disabled={cohortLoading}>
                  <Trans>Annuler</Trans>
                </Button>
                <Button onClick={createCohort} disabled={cohortLoading || !cohortName.trim()}>
                  {cohortLoading ? (
                    <RefreshCw className="h-4 w-4 animate-spin mr-1.5" />
                  ) : (
                    <FolderPlus className="h-4 w-4 mr-1.5" />
                  )}
                  <Trans>Créer la cohorte</Trans>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
